import type { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useVolumeUnit } from "@/hooks/useVolumeUnit";
import { formatVolumeUnit } from "@/lib/format";
import { cn } from "@/lib/cn";

interface Props {
  /** Unique prefix for ids so two cargo rows keep distinct accessible names. */
  idPrefix: string;
  weight: UseFormRegisterReturn;
  volume: UseFormRegisterReturn;
  pallets: UseFormRegisterReturn;
  weightError?: string;
  volumeError?: string;
  palletsError?: string;
  /**
   * A fourth cell on the SAME row — «Стоимость» in the permanent-order mockup.
   *
   * A slot rather than a `cost` prop: the field's shape differs per modal (this
   * one carries a currency suffix), and the row's job is the layout, not the
   * field. Absent, the row stays three columns for every other caller.
   */
  trailing?: import("react").ReactNode;
  /**
   * Renders «Вес, кг» as a COMPUTED, read-only field showing this value.
   *
   * Owner ruling, 2026-08-25: with parcels enabled the order's weight is the
   * sum of the parcel weights, so hand-editing it would let the two disagree.
   * Opt-in and absent by default — the order-detail and template forms have no
   * parcel list to sum and keep a normal editable field.
   *
   * `readOnly` rather than `disabled` on purpose: a disabled input is skipped by
   * the tab order and by many screen readers, so a dispatcher would never learn
   * WHY the number cannot be changed. Read-only keeps it focusable and
   * announced, and the description below says where the value comes from.
   */
  computedWeight?: string;
}

/**
 * Вес / Объём / Паллеты on one row.
 *
 * Steps differ on purpose and match the columns: `weight_kg` and `volume_m3` are
 * `Decimal(_, 3)` on both Order and OrderTemplate, `pallets` is `Decimal(_, 2)`
 * (§12.1). Sending more precision than the column holds is silently truncated
 * server-side, which is worse than being rejected.
 */
export function CargoNumbersRow({
  idPrefix,
  weight,
  volume,
  pallets,
  weightError,
  volumeError,
  palletsError,
  computedWeight,
  trailing,
}: Props) {
  const { t } = useTranslation();
  const volumeUnit = useVolumeUnit();
  const weightIsComputed = computedWeight !== undefined;
  const weightHintId = `${idPrefix}-weight-computed-hint`;

  const inputCls =
    "h-10 w-full rounded-xl border border-gray-200 px-3 text-sm tabular-nums text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";
  const labelCls = "mb-1 block truncate text-xs font-semibold text-gray-700";

  return (
    <div className={cn("grid gap-3", trailing ? "grid-cols-4" : "grid-cols-3")}>
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}-weight`} className={labelCls}>
          {t("orders.form.weight")}
        </label>
        {weightIsComputed ? (
          <>
            {/*
              A computed field must SAY it is computed, not merely look inert.
              A greyed box that swallows keystrokes without explanation is its
              own defect — so this carries `readOnly` + `aria-readonly` (the
              state is announced to a screen reader), plus `title` and
              `aria-describedby` naming where the number comes from.

              The description is `sr-only` rather than a visible line, and that
              is a layout decision with a measured reason: as a visible line it
              added 22px to this row, which at 1280 pushed column 2 6px past its
              track (620 vs 614) and made the COLUMN scroll — breaking the
              owner's «only the parcels list scrolls» rule. The explanation is
              not lost: the ПОСЫЛКИ block directly below carries the same
              sentence visibly («вес — сумма по местам»), so a sighted user
              reads it there while a screen-reader user gets it on the field
              itself. Two visible copies would have been noise anyway.

              `type="text"` rather than `number`: a read-only number input still
              renders its spinner in some browsers, which invites a click that
              does nothing. `inputMode="decimal"` keeps the numeric keypad on
              mobile.
            */}
            <input
              id={`${idPrefix}-weight`}
              type="text"
              inputMode="decimal"
              readOnly
              aria-readonly="true"
              aria-describedby={weightHintId}
              title={t("orders.form.weightFromParcels")}
              value={computedWeight}
              // The grey fill is STRUCTURE — a disabled/inert surface — which is
              // exactly what neutral greys are for (§10.10). It is not a
              // selected or active state, so no brand token belongs here.
              className={cn(inputCls, "bg-gray-50 text-gray-500")}
            />
            <p id={weightHintId} className="sr-only">
              {t("orders.form.weightFromParcels")}
            </p>
          </>
        ) : (
          <input
            id={`${idPrefix}-weight`}
            {...weight}
            type="number"
            min="0"
            step="0.001"
            placeholder="—"
            className={inputCls}
          />
        )}
        {weightError && !weightIsComputed && (
          <p className="mt-1 text-xs text-brand-danger">{weightError}</p>
        )}
      </div>
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}-volume`} className={labelCls}>
          {t("orders.form.volume", { unit: formatVolumeUnit(volumeUnit) })}
        </label>
        <input
          id={`${idPrefix}-volume`}
          {...volume}
          type="number"
          min="0"
          step="0.001"
          placeholder="—"
          className={inputCls}
        />
        {volumeError && (
          <p className="mt-1 text-xs text-brand-danger">{volumeError}</p>
        )}
      </div>
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}-pallets`} className={labelCls}>
          {t("orders.form.pallets")}
        </label>
        <input
          id={`${idPrefix}-pallets`}
          {...pallets}
          type="number"
          min="0"
          step="0.01"
          placeholder="—"
          className={inputCls}
        />
        {palletsError && (
          <p className="mt-1 text-xs text-brand-danger">{palletsError}</p>
        )}
      </div>
      {trailing}
    </div>
  );
}
